/**
 * Hook to check if a user can access a specific site setting section.
 *
 * For Server/Site Admins, access is granted unconditionally (they use the
 * legacy site-settings flow and are not gated by the V2 metadata API).
 *
 * For Admin Profile users (anyone who is neither Server nor Site Admin),
 * access is determined entirely by the V2 site settings metadata API:
 * if the server returns metadata for the section, the user can access it;
 * if not, they cannot.
 *
 * @module useCanAccessSetting
 */

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';

import { useSelector } from '../general-settings/utils';
import { useSiteSettingsMetadata } from './useSiteSettingsMetadata';

/**
 * Check if the current user can access a specific site setting section.
 *
 * Rules:
 * - Server/Site Admins always have access
 * - Admin Profile users: access is determined by the V2 metadata API response
 *   (a section the user can see is returned in sectionMetadata; anything else
 *   is treated as no-access)
 * - While the metadata call is still loading, treat as no-access to avoid
 *   flashing empty section titles
 *
 * @param sectionName - The metadata section name (e.g., 'recycleBin',
 *                      'groupSetsEnabled', 'namedSharingEnabled')
 * @returns true if the user can access the section, false otherwise
 */
export function useCanAccessSetting(sectionName: string): boolean {
  const isSiteOrServerAdmin = useSelector(
    CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector,
  );
  const { sectionMetadata, isLoading } = useSiteSettingsMetadata(sectionName);

  if (isSiteOrServerAdmin) {
    return true;
  }
  if (!FeatureFlags.metadataDrivenSiteSettingsEnabled) {
    return false;
  }
  if (isLoading) {
    return false;
  }
  return sectionMetadata != null;
}

